// src/features/order/Bots/OrderSimulatorBot.ts
import axios, { AxiosError } from 'axios'
import { fetchProductsApi } from '../../stock/api/productApi'
import { CashRegister } from '../../cashRegister/api/cashFlowApi'
import { executeOrderScript, SaleInputPayload } from '../scripts/orderScriptService'
import type { Product } from '../../stock/api/productApi'

interface PaymentMethod {
  payment_method_id: number
  payment_method_name: string
}

// ─── CONFIG ────────────────────────────────────────────────────────────────
const STOCK_API = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:3000'
const NUM_ORDER_ITEMS = 10
const MEAN_QTY = 7
const STDDEV_QTY = 2
const MIN_DELAY_MS = 3_000
const MAX_DELAY_MS = 10_000

let BEARER = ''
let running = false
let timer: ReturnType<typeof setTimeout> | null = null

const api = axios.create({ baseURL: STOCK_API })

function gaussianInt(mean: number, std: number): number {
  const u = Math.random(), v = Math.random()
  return Math.max(
    1,
    Math.round(Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v) * std + mean)
  )
}

function randomDelay(): number {
  return Math.floor(Math.random() * (MAX_DELAY_MS - MIN_DELAY_MS + 1) + MIN_DELAY_MS)
}

function pickDistinct<T>(arr: T[], n: number): T[] {
  const copy = [...arr], out: T[] = []
  while (out.length < n && copy.length) {
    const idx = Math.floor(Math.random() * copy.length)
    out.push(...copy.splice(idx, 1))
  }
  return out
}

async function cycle() {
  try {
    // 1️⃣ Find open shift
    const regsResp = await api.get<{ cash_registers: CashRegister[] }>(
      '/service_cash_flow/registers',
      {
        params: { active_only: true },
        headers: { Authorization: `Bearer ${BEARER}` },
      }
    )
    const open = regsResp.data.cash_registers.find(
      r => r.last_shift_open && r.last_shift_id
    )
    if (!open) {
      console.warn('[OrderBot] no open shift – skipping')
      return
    }

    // 2️⃣ Fetch products
    const products: Product[] = await fetchProductsApi()
    if (!products.length) {
      console.warn('[OrderBot] empty catalog – skipping')
      return
    }

    // 3️⃣ Pick items
    const chosen = pickDistinct(products, NUM_ORDER_ITEMS)
    const items: SaleInputPayload['items'] = chosen.map(p => ({
      id_product:              p.id_product,
      name:                    p.product_name,
      quantity:                gaussianInt(MEAN_QTY, STDDEV_QTY),
      product_price_purchase:  p.product_price_purchase  ?? 0,
      product_price_sales:     p.product_price_sales     ?? 0,
    }))

    // 4️⃣ Pick random payment method
    const payResp = await api.get<{ payment_methods: PaymentMethod[] }>(
      '/service_cash_flow/payment_methods',
      { headers: { Authorization: `Bearer ${BEARER}` } }
    )
    const pmList = payResp.data.payment_methods
    const pm = pmList[Math.floor(Math.random() * pmList.length)]

    // 5️⃣ Build payload & execute
    const total = items.reduce((sum: number, i): number =>
      sum + i.product_price_sales * i.quantity, 0
    )

    const saleInput: SaleInputPayload = {
      items,
      cash_flow: {
        id_register:       open.id_register,
        shift_id:          open.last_shift_id!,
        payment_method_id: pm.payment_method_id,
        total_amount:      total,
      }
    }

    console.info('[OrderBot] SaleInputPayload:', JSON.stringify(saleInput, null, 2))
    await executeOrderScript(saleInput)
    console.info(
      `[OrderBot] posted ${items.length} items, $${total.toFixed(2)}, pm=${pm.payment_method_name}`
    )

  } catch (err) {
    console.error('[OrderBot] cycle failed:', (err as AxiosError).message)
  } finally {
    if (running) timer = setTimeout(cycle, randomDelay())
  }
}

// ─── PUBLIC API ─────────────────────────────────────────────────────────────
export function startOrderBot(token: string) {
  BEARER = token
  if (running) return
  running = true
  timer = setTimeout(cycle, randomDelay())
}

export function stopOrderBot() {
  running = false
  if (timer) clearTimeout(timer)
}

export function isOrderBotRunning() {
  return running
}
